<template>
    <section class="card profile-header-section">
        <div v-if="isAiProcessing" class="ai-alert">
            <el-icon class="is-loading mr-2"><Loading /></el-icon>
            <span>{{ $trans("web_profile.ai_in_progress") || "AI is generating content. Editing is temporarily disabled." }}</span>
        </div>

        <div class="section-title-row">
            <h1 class="section-title">{{ $trans("web_profile.summary") }}</h1>

            <div class="button-group">
                <el-button
                    type="primary"
                    size="small"
                    :loading="isGenerating"
                    :disabled="isAiProcessing"
                    @click="showGenerateModal = true"
                    class="generate-btn"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="mr-2"
                    >
                        <path
                            d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"
                        />
                    </svg>
                    {{
                        $trans("web_profile.generate_summary") || "AI Generate"
                    }}
                </el-button>

                <LogButton 
                    v-if="WebProfileStore.isAdmin" 
                    :onClick="() => WebProfileStore.openVersionHistoryModal('summary')" 
                />

                <EditButton 
                    :onClick="isAiProcessing ? () => {} : WebProfileStore.openEditSummaryModal" 
                    :class="{ 'disabled-action': isAiProcessing }"
                />
            </div>
        </div>
        <div class="details-header">
            <div class="summary-content">
                <div
                    v-if="WebProfileStore.profile.summary || !isGenerating"
                    class="summary-html"
                    v-html="WebProfileStore.profile.summary"
                />

                <el-skeleton
                    v-if="isGenerating && !WebProfileStore.profile.summary"
                    animated
                    :rows="3"
                    style="width: 100%; margin-bottom: 12px"
                />
            </div>
        </div>
        <EditSummaryModal />

        <ConfirmSummaryGenerationModal
            v-model="showGenerateModal"
            @confirm="generateAndSaveSummary"
        />
    </section>
</template>

<script>
import { mapStores } from "pinia";
import { useWebProfileStore } from "@/stores/WebProfileStore";
import { Loading } from "@element-plus/icons-vue";
import EditButton from "./../Components/EditButton.vue";
import LogButton from "./../Components/LogButton.vue";
import EditSummaryModal from "./Modals/EditSummaryModal.vue";
import ConfirmSummaryGenerationModal from "./Modals/ConfirmSummaryGenerationModal.vue";
import VersionHistoryModal from "./Modals/VersionHistoryModal.vue";
import axios from "axios";

export default {
    name: "WebProfileSummary",
    components: {
        EditSummaryModal,
        ConfirmSummaryGenerationModal,
        VersionHistoryModal,
        EditButton,
        LogButton,
        Loading,
    },
    data() {
        return {
            isGenerating: false,
            showGenerateModal: false,
            pollingInterval: null,
        };
    },
    computed: {
        ...mapStores(useWebProfileStore),
        isAiProcessing() {
            return this.isGenerating || this.WebProfileStore?.aiContentStatus === 'in_progress';
        }
    },
    mounted() {
        this.startAiStatusPolling();
    },
    beforeUnmount() {
        this.stopAiStatusPolling();
    },
    methods: {
        startAiStatusPolling() {
            this.pollingInterval = setInterval(async () => {
                if (this.WebProfileStore.aiContentStatus === 'in_progress') {
                    try {
                        const profileId = this.WebProfileStore.profile.id;
                        const response = await axios.get(`/candidates/${profileId}/web-profile/ai-status`);
                        if (response.data && response.data.ai_content_status) {
                            if (response.data.ai_content_status === 'success') {
                                this.WebProfileStore.updateGeneratedContent(response.data);
                            } else {
                                this.WebProfileStore.aiContentStatus = response.data.ai_content_status;
                            }
                        }
                    } catch (e) {
                    }
                }
            }, 5000);
        },
        stopAiStatusPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
            }
        },
        async generateAndSaveSummary() {
            if (this.isGenerating) return;
            this.isGenerating = true;
            this.WebProfileStore.aiContentStatus = 'in_progress';

            try {
                const profileId = this.WebProfileStore.profile.id;
                if (!profileId) return;

                const token = document.head.querySelector('meta[name="csrf-token"]')?.content;

                await axios.post(
                    `/candidates/${profileId}/web-profile/generate/summary`,
                    {},
                    {
                        headers: {
                            "X-CSRF-TOKEN": token,
                            "Content-Type": "application/json",
                        },
                    }
                );

                this.$message.success(
                    this.$trans("web_profile.generation_started_email_notification") || 
                    "Generation started. You will be notified via email when it's done."
                );
                
                this.showGenerateModal = false;
            } catch (error) {
                if (error.response && error.response.status === 423) {
                    this.$message.warning(
                        this.$trans("web_profile.generation_locked") ||
                            "Content generation for this section is currently in progress. Please try again later."
                    );
                } else {
                    console.error("Error starting summary generation:", error);
                    this.$message.error(this.$trans("global.something_went_wrong"));
                }
            } finally {
                this.isGenerating = false;
            }
        },
    },
};
</script>

<style scoped>
.disabled-action {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed !important;
}

.ai-alert {
    background-color: #e6f7ff;
    border: 1px solid #91d5ff;
    padding: 10px 16px;
    border-radius: 4px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    color: #1890ff;
    font-size: 14px;
    font-weight: 500;
}

.generate-btn {
    background-color: #41abc4;
    border-color: #41abc4;
    color: white;
    font-weight: 500;
}

.generate-btn:hover {
    background-color: #3693a9;
    border-color: #3693a9;
}

.generate-btn.is-disabled,
.generate-btn.is-disabled:hover,
.generate-btn.is-disabled:focus,
.generate-btn.is-disabled:active {
    background-color: #999 !important;
    border-color: #999 !important;
    cursor: not-allowed !important;
    pointer-events: auto !important;
}

.details-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    width: 100%;
}

.summary-content {
    flex: 1;
}

.summary-content h2 {
    margin: 0;
    font-size: 16px !important;
    font-weight: 700 !important;
    word-wrap: break-word;
    white-space: pre-wrap;
}

.summary-content .summary-html {
    margin: 0;
    font-size: 16px !important;
    font-weight: 400 !important;
    word-wrap: break-word;
}

.summary-content .summary-html :deep(strong),
.summary-content .summary-html :deep(b) {
    font-weight: 700 !important;
}

.button-group {
    display: flex;
    gap: 10px;
    flex-shrink: 0;
}

.loading-summary {
    display: flex;
    align-items: center;
    gap: 5px;
}

.profile-header-section {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #fff;
}

.el-button {
    background-color: #41abc4;
    border-color: #41abc4;
    height: 34px;
    font-size: 16px;
    font-weight: 700 !important;
}

.loading-summary {
    display: flex;
    align-items: center;
    gap: 8px;
}
.button-group {
    display: flex;
    align-items: center;
    gap: 8px;
}
.details-column {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 1rem;
    width: 100%;
}
.details-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.section-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.section-title {
    margin: 0;
    font-size: 1.875rem;
    font-weight: 600;
}

.typing-dot {
    display: inline-block;
    width: 4px;
    height: 4px;
    background: #41abc4;
    border-radius: 50%;
    margin-left: 4px;
    animation: blink 1s infinite;
}

.empty-text {
    color: #999;
    font-style: italic;
    font-size: 1.2rem;
}

@keyframes blink {
    0% {
        opacity: 0;
    }
    50% {
        opacity: 1;
    }
    100% {
        opacity: 0;
    }
}
</style>